import React from 'react'
import styled from 'styled-components'

import {
  Maybe,
  SanityBlock,
  SanityTable,
  SanityTableRow,
  SanityTableSection,
} from '../../../graphql/gatsby'
import { useSanityRoleBasedContent } from '../../../lib/sanity/hooks/useSanityRoleBasedContent'
import BodyPortableText from '../BodyPortableText'
import { pageSectionStylesFor } from '../styles'
import * as Styled from './styled'

export type LegacySanityTable = {
  __typename?: 'SanityTable'
  _key?: Maybe<string>
  rows?: Maybe<Array<Maybe<LegacySanityTableRow>>>
}
type LegacySanityTableCell = {
  __typename?: 'SanityTableCell'
  _key?: Maybe<string>
  colSpan?: Maybe<number>
  data?: Maybe<LegacySanityTableCellData>
  rowSpan?: Maybe<number>
}

type LegacySanityTableCellData = {
  __typename?: 'SanityTableCellData'
  _key?: string | null
  contents?: Maybe<Array<Maybe<SanityBlock>>>
  heading?: Maybe<boolean>
}

export type LegacySanityTableRow = {
  __typename?: 'SanityTableRow'
  _key?: Maybe<string>
  cells?: Maybe<Array<Maybe<LegacySanityTableCell>>>
}

export interface LegacyTableCellProps extends LegacySanityTableCell {
  className?: string
}

const CellText = styled.p`
  font-family: ${({ theme }) => theme.fontFamilies.gotham};
  font-size: 14px;
`

// TODO: remove once we have migrated all table cells to string contents
const LegacyTableCell: React.VFC<LegacyTableCellProps> = ({
  className,
  colSpan,
  data,
  rowSpan,
}) => {
  if (!data) return null
  const isTableHeader = !!data.heading

  if (isTableHeader) {
    return (
      <Styled.TableHeaderCell className={className} colSpan={colSpan ?? 1} rowSpan={rowSpan ?? 1}>
        {data.contents == null ? null : <BodyPortableText blocks={data.contents} />}
      </Styled.TableHeaderCell>
    )
  }
  return (
    <Styled.TableCell className={className} colSpan={colSpan ?? 1} rowSpan={rowSpan ?? 1}>
      {data.contents == null ? null : <BodyPortableText blocks={data.contents} />}
    </Styled.TableCell>
  )
}

export type TableRowProps = (SanityTableRow | LegacySanityTableRow) & {
  className?: string
  isHeaderRow: boolean | null | undefined
}

// NOTE: We previously used a table plugin for Sanity Studio that supported portable text in table
// cells. However, this table plugin is no longer maintained, and we are migrating to the newer
// and officially supported table plugin, which only supports string contents in table cells. For
// now, we support both portable text and string contents in table cells to ensure a smooth
// migration.

const TableRow: React.VFC<TableRowProps> = ({ className, cells, isHeaderRow }) => (
  <Styled.TableRow className={className}>
    {cells &&
      cells.map((cell, index) =>
        cell == null ? null : typeof cell === 'string' ? (
          isHeaderRow ? (
            <Styled.TableHeaderCell key={cell}>
              <CellText>{cell}</CellText>
            </Styled.TableHeaderCell>
          ) : (
            <Styled.TableCell key={cell}>
              <CellText>{cell}</CellText>
            </Styled.TableCell>
          )
        ) : (
          <LegacyTableCell
            colSpan={cell.colSpan}
            data={cell.data}
            rowSpan={cell.rowSpan}
            key={cell._key ?? index}
          />
        ),
      )}
  </Styled.TableRow>
)

export type TableProps = (SanityTable | LegacySanityTable) & {
  className?: string
  firstRowIsHeader: boolean | null | undefined
}

const Table: React.VFC<TableProps> = ({ className, firstRowIsHeader, rows }) => (
  <Styled.Table className={className}>
    <tbody>
      {rows &&
        rows.map(
          (row, index) =>
            row?.cells && (
              <TableRow
                {...row}
                isHeaderRow={firstRowIsHeader && index === 0}
                key={row._key ?? index}
              />
            ),
        )}
    </tbody>
  </Styled.Table>
)

export interface TableSectionProps extends SanityTableSection {
  className?: string
}

const TableSection: React.VFC<TableSectionProps> = ({
  className,
  firstRowIsHeader,
  roleBasedContent,
  table,
}) => {
  const { shouldDisplay } = useSanityRoleBasedContent({ roleBasedContent })
  if (!shouldDisplay || !table) {
    return null
  }
  return (
    <div className={className}>
      <Table firstRowIsHeader={firstRowIsHeader} rows={table?.rows} />
    </div>
  )
}

const StyledTableSection = styled(TableSection)`
  background-color: ${({ theme }) => theme.colors.grayscale.white};
  ${({ pageSectionStyles }) => pageSectionStylesFor(pageSectionStyles)};
  overflow-x: auto;
  @media (min-width: 768px) {
    padding-left: 4.5rem;
    padding-right: 4.5rem;
  }
  @media (min-width: 992px) {
    padding-left: 5.5rem;
    padding-right: 5.5rem;
  }
`

export default StyledTableSection
